import React, { useState } from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  /** Show first/last jump buttons at the ends. Default true. */
  showFirstLast?: boolean;
  /** Show a numeric "go to page N" input. Default: auto-show when totalPages > 10. */
  showJumpInput?: boolean;
  /** Optional row/item total for the "N rows" footer label. */
  totalCount?: number;
  /** Unit word shown next to totalCount ("rows", "jobs"). */
  unit?: string;
}

/**
 * Canonical pagination control used across the app.
 *
 * Visual language follows the template-picker pattern already in use
 * (``<`` / ``>`` glyphs, blue primary + white secondary, ellipsis for
 * runs). Extended with optional First / Last jump buttons and a "go to
 * page N" input for the long result sets the Migration tab ships.
 *
 * Callers that only need Prev / numeric / Next (no jumps, no count) can
 * pass ``showFirstLast={false}`` and ``showJumpInput={false}`` to get
 * back the original minimal widget.
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  showJumpInput,
  totalCount,
  unit = "rows",
}) => {
  const [jumpInput, setJumpInput] = useState("");

  if (totalPages === 0) {
    return null;
  }

  if (totalPages === 1) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <nav aria-label="Pagination">
          <ul className="flex items-center">
            <li>
              <button
                className="px-3 py-1.5 mx-0.5 text-white bg-blue-600 border border-blue-600 rounded font-semibold"
                disabled
              >
                1
              </button>
            </li>
          </ul>
        </nav>
        {totalCount !== undefined && (
          <span>
            {totalCount.toLocaleString()} {unit}
          </span>
        )}
      </div>
    );
  }

  const pageNumbers = buildPageList(currentPage, totalPages);
  const autoJump = showJumpInput ?? totalPages > 10;

  const goTo = (next: number) => {
    if (next < 1 || next > totalPages || next === currentPage) return;
    onPageChange(next);
  };

  const handleJump = (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (!Number.isFinite(n)) return;
    goTo(Math.min(Math.max(1, n), totalPages));
    setJumpInput("");
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
      {totalCount !== undefined ? (
        <span className="text-gray-500">
          Page <span className="font-medium text-gray-900">{currentPage}</span>{" "}
          of {totalPages.toLocaleString()}
          <span className="ml-2">
            · {totalCount.toLocaleString()} {unit}
          </span>
        </span>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1">
        <nav aria-label="Pagination">
          <ul className="flex items-center">
            {showFirstLast && (
              <li>
                <button
                  type="button"
                  onClick={() => goTo(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                  className={navButtonClass(currentPage === 1, "rounded-l-md")}
                >
                  «
                </button>
              </li>
            )}
            <li>
              <button
                type="button"
                onClick={() => goTo(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className={navButtonClass(
                  currentPage === 1,
                  showFirstLast ? "" : "rounded-l-md"
                )}
              >
                &lt;
              </button>
            </li>
            {pageNumbers.map((page, i) =>
              page === "ellipsis" ? (
                <li key={`e${i}`} aria-hidden="true">
                  <span className="px-3 py-1.5 mx-0.5 text-gray-500">…</span>
                </li>
              ) : (
                <li key={page}>
                  <button
                    type="button"
                    onClick={() => goTo(page)}
                    aria-current={page === currentPage ? "page" : undefined}
                    className={`px-3 py-1.5 mx-0.5 rounded text-sm border transition-colors duration-150 ${
                      page === currentPage
                        ? "bg-blue-600 text-white font-semibold border-blue-600 cursor-default"
                        : "bg-white text-gray-600 border-gray-300 hover:border-blue-500 hover:bg-blue-500 hover:text-white"
                    }`}
                  >
                    {page}
                  </button>
                </li>
              )
            )}
            <li>
              <button
                type="button"
                onClick={() => goTo(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className={navButtonClass(
                  currentPage === totalPages,
                  showFirstLast ? "" : "rounded-r-md"
                )}
              >
                &gt;
              </button>
            </li>
            {showFirstLast && (
              <li>
                <button
                  type="button"
                  onClick={() => goTo(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                  className={navButtonClass(
                    currentPage === totalPages,
                    "rounded-r-md"
                  )}
                >
                  »
                </button>
              </li>
            )}
          </ul>
        </nav>

        {autoJump && (
          <form onSubmit={handleJump} className="flex items-center gap-1 ml-2">
            <label htmlFor="pagination-jump" className="sr-only">
              Go to page
            </label>
            <input
              id="pagination-jump"
              type="number"
              min={1}
              max={totalPages}
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              placeholder="Go to"
              className="w-20 p-1 text-gray-900 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!jumpInput}
              className="px-2 py-1 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40"
            >
              Go
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Shared styling for First / Prev / Next / Last arrow buttons.
const navButtonClass = (disabled: boolean, radius: string) =>
  `px-3 py-1.5 mx-0.5 text-sm border transition-colors duration-150 ${radius} ${
    disabled
      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
      : "bg-white text-gray-600 border-gray-300 hover:border-blue-500 hover:bg-blue-500 hover:text-white"
  }`;

// Windowed page-number list: always shows first + last + ±siblings around
// current, inserting "ellipsis" where there are gaps.
const buildPageList = (
  current: number,
  total: number
): (number | "ellipsis")[] => {
  const maxPages = 10;
  let start = Math.max(1, current - Math.floor(maxPages / 2));
  let end = start + maxPages - 1;
  
  if (end > total) {
    end = total;
    start = Math.max(1, end - maxPages + 1);
  }
  
  const pages: (number | "ellipsis")[] = [];
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
};

export default Pagination;
